import globals from "@lvt/globals";
import { ToggleVal, controlServiceImpl } from "@lvt/streaming";
import {
    Card,
    Divider,
    Fab,
    Fade,
    Grid,
    Link,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Popper,
    IconButton,
    CardHeader,
    Switch,
    Tooltip,
} from "@material-ui/core";
import { Close, OpenInNew, Settings } from "@material-ui/icons";
import clsx from "clsx";
import { useState } from "react";

import client from "@/api/client";
import { RelayAction } from "@/api/streamMetadata";
import { addAlert, Severity } from "@/store/alerts/slice";
import { RootState, useDispatch, useSelector } from "@/store/index";
import { OpenButton } from "@/utilities/types/streaming";

import useActionButtonStyles from "../ActionButton.styles";
import { useStyles } from "./QuickSettingsButtonStyles";


interface Props {
    liveUnitId: number;
    allowHeater: boolean;
    allowSelfService: boolean;
    canConfigure?: boolean;
    heater: RelayAction | null;
    menuAnchor: HTMLElement | null;
    handleMenu: (button: OpenButton) => void;
    open: boolean;
}

export const QuickSettingsButton: React.FC<Props> = ({
    liveUnitId,
    allowHeater,
    allowSelfService,
    canConfigure = false,
    heater,
    menuAnchor,
    handleMenu,
    open,
}) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const [heaterIsOn, setHeaterIsOn] = useState<boolean>(false);
    const streamingId = useSelector((state: RootState) => state.cameras.streamingId);

    const actionButtonClasses = useActionButtonStyles();
    const classes = useStyles();
    const dispatch = useDispatch();

    const id = open ? "popper" : undefined;

    const controlService = controlServiceImpl(client, streamingId!); // This id should never be null

    const handleHeater = async () => {
        const newState = heaterIsOn ? ToggleVal.Off : ToggleVal.On;

        if (heater) {
            try {
                // Should this use the heater command?
                await controlService.relay(newState, heater.relayName);

                setHeaterIsOn((prevState) => !prevState);
            } catch (e) {
                dispatch(
                    addAlert({
                        severity: Severity.Error,
                        message: `${e}`,
                    }),
                );
            }
        }
    };

    const handleClick = () => {
        setShowTooltip(false);
        handleMenu(OpenButton.QuickSettings);
    };

    return (
        <>
            <Grid item className={actionButtonClasses.fabItem}>
                <Tooltip
                    PopperProps={{ disablePortal: true }}
                    open={showTooltip}
                    disableHoverListener
                    onMouseEnter={() => !open && setShowTooltip(true)}
                    onMouseLeave={() => setShowTooltip(false)}
                    title="Quick Settings"
                    placement="top"
                >
                    <Fab
                        id={"streaming_quick-settings-button"}
                        className={clsx(actionButtonClasses.fab, open && actionButtonClasses.active)}
                        onClick={handleClick}
                        aria-label="quick-settings"
                    >
                        <Settings />
                    </Fab>
                </Tooltip>
            </Grid>

            <Popper
                id={id}
                data-testid="quick-settings-button_popper"
                disablePortal
                anchorEl={menuAnchor}
                className={classes.popper}
                open={open}
                placement="top-end"
                transition
            >
                {({ TransitionProps }) => (
                    <Fade {...TransitionProps}>
                        <Card raised className={classes.menu}>
                            <IconButton
                                id={"streaming_quick-settings-button_close-button"}
                                data-testid="quick-settings-button_close-button"
                                className={classes.close}
                                onClick={() => handleMenu(OpenButton.None)}
                            >
                                <Close />
                            </IconButton>
                            <CardHeader title="Quick Settings" />
                            <Divider className={classes.divider} />
                            <List>
                                {allowHeater && (
                                    <ListItem>
                                        <ListItemText primary="Heater" />
                                        <ListItemSecondaryAction>
                                            <Switch
                                                checked={heaterIsOn}
                                                onChange={handleHeater}
                                                inputProps={{ "aria-label": "heater switch" }}
                                                edge="end"
                                            />
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                )}

                                {allowSelfService && (
                                    <>
                                        {canConfigure && (
                                            <Link
                                                id={"streaming_quick-settings-button_live-unit-settings-link"}
                                                href={`${globals.servicePortal.url}/live-unit/${liveUnitId}`}
                                                underline="none"
                                                color="inherit"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                <ListItem className={classes.settingsLink}>
                                                    <ListItemText primary="Live Unit Settings" />
                                                    <ListItemSecondaryAction>
                                                        <OpenInNew />
                                                    </ListItemSecondaryAction>
                                                </ListItem>
                                            </Link>
                                        )}

                                        <Link
                                            id={"streaming_quick-settings-button_camera-self-service-settings-link"}
                                            href={`${globals.servicePortal.url}/camera/${streamingId}`}
                                            underline="none"
                                            color="inherit"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <ListItem className={classes.settingsLink}>
                                                <ListItemText primary="Camera Settings" />
                                                <ListItemSecondaryAction>
                                                    <OpenInNew />
                                                </ListItemSecondaryAction>
                                            </ListItem>
                                        </Link>
                                    </>
                                )}
                            </List>
                        </Card>
                    </Fade>
                )}
            </Popper>
        </>
    );
};
